import { List, Model, ModalUtils, NeoModel, NotifyUtils, Misc } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { AppService, Types } from '../../../App/Services/AppService';
import ServiceProviderFileGroupingLookup from '../Models/Lookups/ServiceProviderFileGroupingLookup';
import SFTPBrokerDetailsLookup from '../Models/Lookups/SFTPBrokerDetailsLookup';
import SftpDetailsLookup from '../Models/Lookups/SftpDetailsLookup';
import StaticServiceProviderModel from '../../../Common/Models/StaticServiceProviderModel';
import { DaysOfWeekEnum } from '../../../Common/Enums/DaysOfWeekEnum';
import type { ExecuteLBVHCommandResult, LBVHLookupResponse } from '../../ApiClients/ServiceProviderApiClient';
import { TextConstants } from '../../../Common/TextConstants';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import SaveFileGroupingExportSettingCommand from '../Models/Commands/SaveFileGroupingExportSettingCommand';
import { ExportEventTypeEnum } from '../Models/Enums/ExportEventTypeEnum';
import ServiceProviderFileGroupingExportSettingLookup from '../Models/Lookups/ServiceProviderFileGroupingExportSettingLookup';

@NeoModel
export default class ManageServiceProviderFileGroupingComponentVM extends Views.ViewModelBase {
    private exportSettingIdsByItem = new WeakMap<ServiceProviderFileGroupingExportSettingLookup, { [day: string]: number }>();
    private managedExportSettingIdsByDay: { [day: string]: number } = {};

    constructor(
        public serviceProviderApiClient = AppService.get(Types.ApiClients.ServiceProviderApiClient),
        public generalApiClient = AppService.get(Types.ApiClients.GeneralApiClient),
        public exportDataApiClient = AppService.get(Types.ApiClients.ExportDataApiClient),
        taskRunner = AppService.get(Types.Neo.TaskRunner)) {
        super(taskRunner);
    }

    public managedServiceProviderFileGrouping = new ServiceProviderFileGroupingLookup();
    public sftpBrokerDetailsList = new List(SFTPBrokerDetailsLookup);
    public sftpDetailsList = new List(SftpDetailsLookup);
    public showManageServiceProviderFileGroupingInd: boolean = false;
    public serviceProviderList = new List(StaticServiceProviderModel);
    public selectedServiceProvider = new StaticServiceProviderModel();
    public isReadOnlyInd: boolean = false;
    public addNewServiceProviderInd: boolean = false;
    public exportSettingsList = new List(ServiceProviderFileGroupingExportSettingLookup);

    public currentExportSetting = new ServiceProviderFileGroupingExportSettingLookup();
    public currentSFTPSetting = new SFTPBrokerDetailsLookup();

    public isEditingExportSettingInd: boolean = false;
    public isEditingSFTPSettingInd: boolean = false;

    public editingExportSettingIndex: number = -1;
    public editingSFTPSettingIndex: number = -1;
    public deletedSFTPSettingIds: number[] = [];

    public hasUnsavedExportSettingsChanges: boolean = false;
    public hasUnsavedSFTPSettingsChanges: boolean = false;

    public async initialise() {
        this.resetExportSettingEditorState();
        this.resetSFTPEditorState();
        this.showManageServiceProviderFileGroupingInd = true;
        await this.setServiceProviderList();
        await this.populateSFTPDetailsList();

        const response = await this.serviceProviderApiClient.getSFTPBrokerDetails(this.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId);
        this.sftpBrokerDetailsList.set(response.data);
        await this.loadExportSettingsForBroker();
    }

    public async populateSFTPDetailsList() {
        const response = await this.taskRunner.waitFor(this.generalApiClient.getSftpDetailsList());
        this.sftpDetailsList.set(response.data);
    }

    public get hasUnsavedChanges(): boolean {
        return this.hasUnsavedExportSettingsChanges
            || this.hasUnsavedSFTPSettingsChanges
            || this.managedServiceProviderFileGrouping.isDirty
    }

    public getDaysOfWeekEnumList(searchString?: string) {
        let map: { id: number, name: string }[] = [];
        let keys = Object.keys(DaysOfWeekEnum).filter((v) => isNaN(Number(v)));
        keys.forEach((key, index) => {
            if (searchString === undefined || (searchString !== undefined && key.toString().includes(searchString!))) {
                map.push({ id: index, name: key.toString() });
            }
        })
        return map;
    }

    public async loadExportSettingsForBroker() {
        if (this.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId) {
            const currentBroker = await this.taskRunner.waitFor(this.serviceProviderApiClient.getServiceProviderFileGroupingLookupById(this.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId));
            const currentBrokerData = currentBroker.data as ServiceProviderFileGroupingLookup;
            const brokerExportSettings: ServiceProviderFileGroupingExportSettingLookup[] = currentBrokerData.exportSettingLookupList || [];

            this.exportSettingsList.set([]);

            if (brokerExportSettings.length > 0) {
                const groupedSettings = new Map<string, ServiceProviderFileGroupingExportSettingLookup>();

                brokerExportSettings.forEach((setting) => {
                    const eventTime = this.normalizeLBVHRun(setting.eventTime);
                    const eventType = setting.exportEventType ?? setting.eventType ?? setting.staticExportEventTypeId ?? ExportEventTypeEnum.Export;
                    const groupKey = `${eventType}|${this.getTimeKey(eventTime)}`;
                    const dayList = setting.dayOfWeek ? [setting.dayOfWeek] : (setting.daysOfWeek || []);

                    let grouped = groupedSettings.get(groupKey);
                    if (!grouped) {
                        grouped = new ServiceProviderFileGroupingExportSettingLookup();
                        grouped.staticServiceProviderFileGroupingId = setting.staticServiceProviderFileGroupingId ?? this.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId;
                        grouped.eventTime = eventTime;
                        grouped.eventType = eventType;
                        grouped.daysOfWeek = [];
                        this.exportSettingIdsByItem.set(grouped, {});
                        groupedSettings.set(groupKey, grouped);
                    }

                    dayList.forEach((day) => {
                        if (!grouped!.daysOfWeek.includes(day)) {
                            grouped!.daysOfWeek.push(day);
                        }

                        if (setting.staticServiceProviderFileGroupingExportSettingId) {
                            const currentIds = this.exportSettingIdsByItem.get(grouped!) || {};
                            currentIds[day] = setting.staticServiceProviderFileGroupingExportSettingId;
                            this.exportSettingIdsByItem.set(grouped!, currentIds);
                        }
                    });
                });

                groupedSettings.forEach((grouped) => {
                    this.populateSelectedDays(grouped);
                    this.exportSettingsList.push(grouped);
                });
            }

            this.resetExportSettingEditorState();
        }
    }

    private populateSelectedDays(setting: ServiceProviderFileGroupingExportSettingLookup) {

        const enumMap = new Map(this.getDaysOfWeekEnumList().map(e => [e.name, e]));
        const selectedDays: Model.ISelectedItem[] = [];
        setting.daysOfWeek.forEach((day: string) => {
            const enumItem = enumMap.get(day);
            if (enumItem) {
                selectedDays.push({ id: enumItem.id, display: enumItem.name });
            }
        });
        setting.selectedDaysOfWeek = selectedDays;
        setting.daysOfWeekNumbers = setting.selectedDaysOfWeek.map((x: Model.ISelectedItem) => Number(x.id));
    }

    private getTimeKey(value: Date | null): string {
        if (!value) {
            return '';
        }

        const hh = value.getHours().toString().padStart(2, '0');
        const mm = value.getMinutes().toString().padStart(2, '0');
        const ss = value.getSeconds().toString().padStart(2, '0');
        return `${hh}:${mm}:${ss}`;
    }

    public addNewExportSetting() {
        this.currentExportSetting = new ServiceProviderFileGroupingExportSettingLookup();
        this.managedExportSettingIdsByDay = {};
        this.editingExportSettingIndex = -1;
        this.isEditingExportSettingInd = true;
    }

    public editExportSetting(item: ServiceProviderFileGroupingExportSettingLookup) {
        this.currentExportSetting = new ServiceProviderFileGroupingExportSettingLookup().mapFrom(item);
        this.managedExportSettingIdsByDay = { ...(this.exportSettingIdsByItem.get(item) || {}) };
        this.isEditingExportSettingInd = true;
        this.editingExportSettingIndex = this.exportSettingsList.indexOf(item);
    }

    public confirmEditExportSetting() {
        const selectedDays: Model.ISelectedItem[] = this.currentExportSetting.selectedDaysOfWeek || [];

        this.currentExportSetting.daysOfWeek = selectedDays.map((x: Model.ISelectedItem) => x.display);
        this.currentExportSetting.daysOfWeekNumbers = selectedDays.map((x: Model.ISelectedItem) => Number(x.id));

        const command = new SaveFileGroupingExportSettingCommand();
        command.exportEventType = this.currentExportSetting.eventType;
        command.eventType = this.currentExportSetting.eventType;
        command.staticExportEventTypeId = this.currentExportSetting.eventType ? Number(this.currentExportSetting.eventType) : null;
        command.eventTime = this.currentExportSetting.eventTime;
        command.eventTimeString = this.getTimeKey(this.currentExportSetting.eventTime);
        command.dayOfWeek = this.currentExportSetting.daysOfWeek.length > 0 ? this.currentExportSetting.daysOfWeek[0] : "";

        if (!command.validator.isValid) {
            const brokenRules = command.validator.getBrokenRules();
            if (brokenRules.propertyRules && brokenRules.propertyRules.length > 0) {
                const errorMessage = brokenRules.propertyRules[0].displayInfo.displayText;
                NotifyUtils.addDanger(TextConstants.Titles.Error, errorMessage, NotificationDuration.Standard);
            }
            return;
        }

        if (this.editingExportSettingIndex < 0) {
            this.exportSettingsList.push(this.currentExportSetting);
            this.exportSettingIdsByItem.set(this.currentExportSetting, { ...this.managedExportSettingIdsByDay });
        } else {
            const existingExportSetting = this.exportSettingsList[this.editingExportSettingIndex];
            if (existingExportSetting) {
                existingExportSetting.mapFrom(this.currentExportSetting);
                this.exportSettingIdsByItem.set(existingExportSetting, { ...this.managedExportSettingIdsByDay });
            }
        }

        this.hasUnsavedExportSettingsChanges = true;
        this.resetExportSettingEditorState();
    }

    public confirmDiscardChanges() {
        this.hasUnsavedExportSettingsChanges = false;
        this.hasUnsavedSFTPSettingsChanges = false;
    }

    public resetExportSettingEditorState() {
        this.currentExportSetting = new ServiceProviderFileGroupingExportSettingLookup();
        this.managedExportSettingIdsByDay = {};
        this.isEditingExportSettingInd = false;
        this.editingExportSettingIndex = -1;
    }

    public async removeExportSetting(item: ServiceProviderFileGroupingExportSettingLookup) {
        const result = await ModalUtils.showYesNo(
            TextConstants.Buttons.Remove,
            'Are you sure you want to remove this export setting?'
        );

        if (result === Misc.ModalResult.Yes) {
            this.exportSettingsList.remove(item);
            this.hasUnsavedExportSettingsChanges = true;

            if (this.editingExportSettingIndex >= 0) {
                this.resetExportSettingEditorState();
            }
        }
    }

    public getExportDaysDisplay(item: ServiceProviderFileGroupingExportSettingLookup): string {
        return (item.daysOfWeek || []).join(', ');
    }

    public getEventTimeDisplay(item: ServiceProviderFileGroupingExportSettingLookup): string {
        if (!item.eventTime) {
            return '';
        }

        const date = item.eventTime instanceof Date ? item.eventTime : new Date(item.eventTime);
        if (isNaN(date.getTime())) {
            return '';
        }

        const hh = date.getHours().toString().padStart(2, '0');
        const mm = date.getMinutes().toString().padStart(2, '0');
        return `${hh}:${mm}`;
    }

    public getEventTypeDisplay(item: ServiceProviderFileGroupingExportSettingLookup): string {
        switch (item.eventType) {
            case ExportEventTypeEnum.Export:
                return 'Export';
            case ExportEventTypeEnum.LatestBookValueHolding:
                return 'Latest Book Value Holding';
            case ExportEventTypeEnum.DailySync:
                return 'Daily Sync';
            default:
                return '';
        }
    }

    public getSaveExportSettingCommandList(): SaveFileGroupingExportSettingCommand[] {
        const fileGroupingId = this.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId;
        const mappedFileGroupingId = fileGroupingId > 0 ? fileGroupingId : 0;

        return this.exportSettingsList.reduce<SaveFileGroupingExportSettingCommand[]>((saveFileGroupingExportSettingCommands, setting) => {
            const exportSettingIdsByDay = this.exportSettingIdsByItem.get(setting) || {};

            (setting.daysOfWeek || []).forEach((dayOfWeek) => {
                const command = new SaveFileGroupingExportSettingCommand();
                command.staticServiceProviderFileGroupingExportSettingId = exportSettingIdsByDay[dayOfWeek] ?? null;
                command.staticServiceProviderFileGroupingId = mappedFileGroupingId;
                command.exportEventType = setting.eventType;
                command.eventType = setting.eventType;
                command.staticExportEventTypeId = setting.eventType ? Number(setting.eventType) : null;
                command.dayOfWeek = dayOfWeek;
                command.eventTime = this.normalizeEventTimeForSave(setting.eventTime);
                command.eventTimeString = this.getTimeKey(setting.eventTime);
                saveFileGroupingExportSettingCommands.push(command);
            });

            return saveFileGroupingExportSettingCommands;
            }, []);
    }

    public addNewSFTPDetails(staticServiceProviderFileGroupingId: number = 0) {
        const sftpBrokerDetails = new SFTPBrokerDetailsLookup();
        sftpBrokerDetails.staticServiceProviderSftpdetailsId = 0;
        sftpBrokerDetails.staticServiceProviderFileGroupingId = staticServiceProviderFileGroupingId;

        this.currentSFTPSetting = sftpBrokerDetails;
        this.editingSFTPSettingIndex = -1;
        this.isEditingSFTPSettingInd = true;
    }

    public editSFTPDetails(sftpBrokerDetails: SFTPBrokerDetailsLookup) {
        this.currentSFTPSetting = new SFTPBrokerDetailsLookup().mapFrom(sftpBrokerDetails);
        this.editingSFTPSettingIndex = this.sftpBrokerDetailsList.indexOf(sftpBrokerDetails);
        this.isEditingSFTPSettingInd = true;
    }

    public setSelectedSFTPDetail() {
        const selected = this.sftpDetailsList.find((x) => x.sftpdetailId === this.currentSFTPSetting.sftpDetailId);
        if (selected) {
            this.currentSFTPSetting.sftpSetting = selected.sftpSetting;
        }
    }

    public confirmEditSFTPSetting() {
        if (!this.currentSFTPSetting.validator.isValid) {
            const brokenRules = this.currentSFTPSetting.validator.getBrokenRules();
            if (brokenRules.propertyRules && brokenRules.propertyRules.length > 0) {
                const errorMessage = brokenRules.propertyRules[0].displayInfo.displayText;
                NotifyUtils.addDanger(TextConstants.Titles.Error, errorMessage, NotificationDuration.Standard);
            }
            return;
        }

        if (this.editingSFTPSettingIndex < 0) {
            this.sftpBrokerDetailsList.push(this.currentSFTPSetting);
        } else {
            const editedSFTPSetting = this.sftpBrokerDetailsList[this.editingSFTPSettingIndex];
            if (editedSFTPSetting) {
                editedSFTPSetting.mapFrom(this.currentSFTPSetting);
            }
        }

        this.hasUnsavedSFTPSettingsChanges = true;
        this.resetSFTPEditorState();
    }

    public async removeSFTPSetting(item: SFTPBrokerDetailsLookup) {
        const result = await ModalUtils.showYesNo(
            TextConstants.Buttons.Remove,
            'Are you sure you want to remove this SFTP setting?'
        );

        if (result === Misc.ModalResult.Yes) {
            this.sftpBrokerDetailsList.remove(item);
            this.deletedSFTPSettingIds.push(item.staticServiceProviderSftpdetailsId);
            this.hasUnsavedSFTPSettingsChanges = true;

            if (this.editingSFTPSettingIndex >= 0) {
                this.resetSFTPEditorState();
            }
        }
    }

    public resetSFTPEditorState() {
        this.currentSFTPSetting = new SFTPBrokerDetailsLookup();
        this.isEditingSFTPSettingInd = false;
        this.editingSFTPSettingIndex = -1;
    }

    public async executeLBVH() {
        await this.taskRunner.run(async () => {
            const fileGroupingId = this.managedServiceProviderFileGrouping.staticServiceProviderFileGroupingId;
            try {
                const response = await this.serviceProviderApiClient.executeLBVH(fileGroupingId);
                const responseData: ExecuteLBVHCommandResult = response.data;

                if (!responseData?.success) {
                    NotifyUtils.addDanger(
                        TextConstants.Titles.Error,
                        responseData?.error || responseData?.message || 'LBVH execution failed.',
                        NotificationDuration.Long
                    );
                    return;
                }

                NotifyUtils.addSuccess(
                    TextConstants.Titles.Success,
                    `LBVH executed successfully for ${this.managedServiceProviderFileGrouping.serviceProviderGroupingName} (ID: ${fileGroupingId}).`,
                    NotificationDuration.Standard
                );
                if (!responseData.data) {
                    throw new Error('LBVH execute returned success but no lookup data.');
                }

                this.applyLBVHLookup(responseData.data);
            } catch (error: any) {
                // Error executing LBVH request itself — show an error

                let errorMessage = 'An error occurred while executing LBVH.';
                try {
                    if (error?.response) {
                        errorMessage =
                            error.response.data?.error ||
                            error.response.data?.message ||
                            `HTTP ${error.response.status} ${error.response.statusText}`;
                        if (error.response.data && typeof error.response.data !== 'string') {
                            errorMessage += ` — ${JSON.stringify(error.response.data)}`;
                        }
                    } else if (error?.message) {
                        errorMessage = error.message;
                    }
                } catch (e) {
                    // ignore stringify errors
                }

                NotifyUtils.addDanger(
                    TextConstants.Titles.Error,
                    errorMessage,
                    NotificationDuration.Long
                );
            }
        });
    }

    private applyLBVHLookup(lookup: LBVHLookupResponse) {
        this.managedServiceProviderFileGrouping.lastLBVHRun = this.normalizeLBVHRun(lookup.lastLBVHRun);
        this.managedServiceProviderFileGrouping.lastLBVHVariant = lookup.lastLBVHVariant ?? this.managedServiceProviderFileGrouping.lastLBVHVariant;
        this.managedServiceProviderFileGrouping.lastLBVHRelative = lookup.lastLBVHRelative ?? this.managedServiceProviderFileGrouping.lastLBVHRelative;
        this.managedServiceProviderFileGrouping.lastLBVHDisplay = lookup.lastLBVHDisplay ?? this.managedServiceProviderFileGrouping.lastLBVHDisplay;
    }

    private normalizeLBVHRun(lastLBVHRun?: Date | string | null): Date | null {
        if (!lastLBVHRun) {
            return null;
        }

        if (lastLBVHRun instanceof Date) {
            return this.normalizeEventTimeForSave(lastLBVHRun);
        }

        const timeOnlyMatch = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(lastLBVHRun);
        if (timeOnlyMatch) {
            const hours = Number(timeOnlyMatch[1]);
            const minutes = Number(timeOnlyMatch[2]);
            const seconds = Number(timeOnlyMatch[3] || '0');
            return new Date(1900, 0, 1, hours, minutes, seconds, 0);
        }

        const parsedDate = new Date(lastLBVHRun);
        if (isNaN(parsedDate.getTime())) {
            return null;
        }

        return this.normalizeEventTimeForSave(parsedDate);
    }

    private normalizeEventTimeForSave(value: Date | null): Date | null {
        if (!value) {
            return null;
        }

        const hours = value.getHours();
        const minutes = value.getMinutes();
        const seconds = value.getSeconds();

        // SQL datetime does not support years earlier than 1753.
        if (value.getFullYear() < 1753) {
            return new Date(1900, 0, 1, hours, minutes, seconds, 0);
        }

        return value;
    }

    public async setServiceProviderList() {
        const response = await this.serviceProviderApiClient.getServiceProviderListByCriteria(" ");
        this.serviceProviderList.set(response.data);

        if (!this.serviceProviderList.find(x => x.serviceProviderName === "NEW")) {
            const newSSPFG = new StaticServiceProviderModel();
            newSSPFG.staticServiceProviderId = 0;
            newSSPFG.serviceProviderName = "NEW";
            this.serviceProviderList.push(newSSPFG);
        }
    }

    public setSelectedServiceProvider(serviceProviderId: number) {
        this.selectedServiceProvider = this.serviceProviderList.find(x => x.staticServiceProviderId === serviceProviderId)!;

        if (serviceProviderId !== 0) {
            this.isReadOnlyInd = true;
            this.managedServiceProviderFileGrouping.meta.serviceProviderCode.value = this.selectedServiceProvider.meta.serviceProviderCode.value;
            this.managedServiceProviderFileGrouping.meta.serviceProviderName.value = this.selectedServiceProvider.meta.serviceProviderName.value;
            this.managedServiceProviderFileGrouping.meta.serverName.value = this.selectedServiceProvider.meta.serverName.value;
            this.managedServiceProviderFileGrouping.meta.serviceProviderSubAccountName.value = this.selectedServiceProvider.meta.serviceProviderSubAccountName.value;
        }
        else if (serviceProviderId === 0) {
            this.isReadOnlyInd = false;
            this.managedServiceProviderFileGrouping.meta.serviceProviderName.value = "";
            this.managedServiceProviderFileGrouping.meta.serviceProviderCode.value = "";
            this.managedServiceProviderFileGrouping.meta.serverName.value = "";
            this.managedServiceProviderFileGrouping.meta.serviceProviderSubAccountName.value = "";
        }
    }

    public async confirmExecuteLBVH() {
        const result = await ModalUtils.showYesNo(
            TextConstants.Buttons.ExecuteLBVH,
            `Execute LBVH for ${this.managedServiceProviderFileGrouping.serviceProviderGroupingName}? This will trigger the LBVH process immediately.`
        );

        if (result === Misc.ModalResult.Yes) {
            await this.executeLBVH();
        }
    }

    public get hasLastLBVH(): boolean {
        return !!this.managedServiceProviderFileGrouping.lastLBVHRun;
    }

    public get lastLBVHVariant(): string {
        return this.managedServiceProviderFileGrouping.lastLBVHVariant;
    }

    public get lastLBVHRelative(): string {
        return this.managedServiceProviderFileGrouping.lastLBVHRelative;
    }

    public get lastLBVHDisplay(): string {
        return this.managedServiceProviderFileGrouping.lastLBVHDisplay;
    }

    public async dailySyncForGrouping(item: ServiceProviderFileGroupingLookup) {
        await this.taskRunner.run(async () => {
            const response = await this.exportDataApiClient.executeDailySync(item.staticServiceProviderFileGroupingId);

            if (response.status === 200) {
                NotifyUtils.addSuccess(TextConstants.Titles.Success, "Daily sync started successfully.", NotificationDuration.Standard);
            }
            else {
                NotifyUtils.addDanger(TextConstants.Titles.Error, response.data.error, NotificationDuration.Long);
            }
        });
    }
}